import { useState, useEffect, useRef } from 'react'
import { Alert, Button, Collapse, IconButton } from '@mui/material'
import { AccountCircle, Close } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'

// Closing the banner suppresses it for 30 minutes, then it returns.
const SUPPRESS_KEY = 'profileReminder.dismissedUntil'
const SUPPRESS_MS = 30 * 60 * 1000

function suppressedRemainingMs(): number {
  try {
    const until = Number(localStorage.getItem(SUPPRESS_KEY) || 0)
    const remaining = until - Date.now()
    return remaining > 0 ? remaining : 0
  } catch {
    return 0
  }
}

/**
 * Full-width reminder rendered under the top nav on every authenticated page,
 * nudging the user to finish their profile: set a display name (so others recognize
 * them) and set a home location (so their location stays private near home). Only
 * the missing items are shown. Closing it (X) hides it for 30 minutes — persisted in
 * localStorage and re-shown by a timer when the window elapses.
 *
 * `displayNameIsSet === false` and null home coords come from the profile/login
 * response (older persisted sessions may have displayNameIsSet undefined and won't
 * nag on the name until refresh).
 */
export function ProfileCompletionBanner() {
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)

  const needsName = isAuthenticated && user?.displayNameIsSet === false
  const needsHome = isAuthenticated && !!user && user.homeLatitude == null && user.homeLongitude == null
  const show = needsName || needsHome

  const [suppressed, setSuppressed] = useState(() => suppressedRemainingMs() > 0)
  const timerRef = useRef<number | undefined>(undefined)

  // Sync suppression to the stored deadline and schedule an automatic re-show.
  useEffect(() => {
    window.clearTimeout(timerRef.current)
    if (!show) return
    const remaining = suppressedRemainingMs()
    setSuppressed(remaining > 0)
    if (remaining > 0) {
      timerRef.current = window.setTimeout(() => setSuppressed(false), remaining)
    }
    return () => window.clearTimeout(timerRef.current)
  }, [show])

  if (!show || suppressed) return null

  const handleClose = () => {
    try {
      localStorage.setItem(SUPPRESS_KEY, String(Date.now() + SUPPRESS_MS))
    } catch {
      // ignore storage errors (private mode, etc.)
    }
    setSuppressed(true)
    window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => setSuppressed(false), SUPPRESS_MS)
  }

  const message =
    needsName && needsHome
      ? 'Finish your profile — add a display name and set your home location to keep it private.'
      : needsName
        ? 'Finish your profile — add a display name so others recognize you.'
        : 'Protect your privacy — set your home location so it stays hidden near home.'

  return (
    <Collapse in>
      <Alert
        severity="info"
        icon={<AccountCircle />}
        sx={{ borderRadius: 0, py: 0.5, '& .MuiAlert-action': { alignItems: 'center', pt: 0 } }}
        action={
          <>
            {needsName && (
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate('/profile?edit=displayName')}
              >
                Set name
              </Button>
            )}
            {needsHome && (
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate('/settings/privacy')}
              >
                Set home location
              </Button>
            )}
            <IconButton color="inherit" size="small" aria-label="Dismiss" onClick={handleClose}>
              <Close fontSize="small" />
            </IconButton>
          </>
        }
      >
        {message}
      </Alert>
    </Collapse>
  )
}
